import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  Box,
  BreadcrumbItem,
  BreadcrumbLink,
  VStack,
  Text,
  HStack,
  Image,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Divider,
  UnorderedList,
  ListItem,
  Skeleton,
  SkeletonText,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useState } from "react";
import { BusinessBreadcrumbs } from "../../components/Breadcrumbs/BusinessBreadcrumbs";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { PageContent } from "../../components/PageContent/PageContent";
import { ChevronUpIcon } from "@chakra-ui/icons";
import { ErrorMessage } from "../../components/ErrorMessage/ErrorMessage";
import { MembershipRenewalForm } from "../../forms/MembershipRenewalForm";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { useDidUnmount } from "../../hooks/useDidUnmount";
import { useAdminApiRequest } from "../../hooks/useApiRequest";
import { OdmaUrlType } from "../../enums/OdmaUrlType";
import { useToast } from "../../hooks/useToast";
import { CopyText } from "../../components/Utilities/CopyText";
import { PreviewButton } from "../../components/Utilities/PreviewButton";
import { ViewControl } from "../../components/ViewControl/ViewControl";
import { BusinessMenu } from "../../components/Business/BusinessMenu";
import { getPaymentProviderLabel } from "../../enums/PaymentProvider";

type Action = "Edit" | "Create";

interface MembershipRenewalDetailProps {
  create?: boolean;
}

export const MembershipRenewalDetail = (
  props: MembershipRenewalDetailProps
) => {
  const [searchParams] = useSearchParams();
  const { businessId, membershipRenewalId } = useParams();
  const [membershipRenewal, setMembershipRenewal] =
    useState<CherryHubAdminApi.MembershipRenewalFormConfigurationItem | null>();
  const [editMode, setEditMode] = useState<boolean>(
    props.create ??
      (searchParams.get("edit") !== null &&
      (searchParams.get("edit") === "" || searchParams.get("edit") == "true")
        ? true
        : false)
  );
  const navigate = useNavigate();
  const didUnmount = useDidUnmount();
  const { showErrorToast, showSuccessToast } = useToast();

  const {
    data: membershipRenewalDoc,
    isLoading,
    error: membershipRenewalError,
  } = useAdminApiRequest(
    (apiClient) => {
      if (!businessId || !membershipRenewalId) {
        return null;
      }
      return apiClient.getMembershipRenewalFormConfiguration(
        businessId,
        membershipRenewalId
      );
    },
    [businessId, membershipRenewalId]
  );

  useEffect(() => {
    setMembershipRenewal(membershipRenewalDoc);
  }, [membershipRenewalDoc]);

  if (membershipRenewalError)
    return <ErrorMessage>{membershipRenewalError}</ErrorMessage>;

  const onSuccess = useCallback(
    (
      membershipRenewal: CherryHubAdminApi.MembershipRenewalFormConfigurationItem
    ) => {
      setMembershipRenewal(membershipRenewal);
      setEditMode(false);
      if (props.create) {
        showSuccessToast("Membership Renewal created.");
        navigate(
          `/admin/businesses/${businessId}/membership-renewals/${membershipRenewal.Meta?.Id}`
        );
      } else {
        showSuccessToast("Membership Renewal updated.");
      }
    },
    [showSuccessToast, membershipRenewal]
  );

  const onFailure = useCallback(
    (message?: string) => {
      showErrorToast(message ?? "Membership Renewal update failed.");
    },
    [showErrorToast]
  );

  const onCancel = () => {
    props.create
      ? navigate(`/admin/businesses/${businessId}/membership-renewals`)
      : setEditMode(false);
  };

  const { data: odmaUrl, isLoading: isOdmaUrlLoading } = useAdminApiRequest(
    (adminApiClient) => {
      if (!businessId || !membershipRenewalId) {
        return null;
      }

      return adminApiClient.getOdmaUrl(
        businessId!,
        OdmaUrlType.MembershipRenewal,
        membershipRenewalId!
      );
    },
    [businessId, membershipRenewalId]
  );

  return (
    <>
      <PageHeading>
        <PageHeading.Title>
          {props.create ? "Create" : "Edit"} Membership Renewal Form
          Configuration
        </PageHeading.Title>
      </PageHeading>
      <PageContent>
        <VStack alignItems="flex-start" w="100%">
          <BusinessBreadcrumbs variant="pageHeading">
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${businessId}/membership-renewals`}
              >
                Membership Renewals
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbLink
                as={Link}
                to={`/admin/businesses/${businessId}/membership-renewals/${
                  props.create ? "create" : membershipRenewal?.id
                }`}
              >
                {props.create ? "New" : membershipRenewal?.FriendlyName}
              </BreadcrumbLink>
            </BreadcrumbItem>
          </BusinessBreadcrumbs>

          {(isLoading || isOdmaUrlLoading || !membershipRenewal) &&
            !props.create && (
              <VStack
                w="100%"
                p={6}
                boxShadow="lg"
                alignItems="flex-start"
                spacing={4}
              >
                <HStack w="100%" justifyContent="flex-start" spacing={4}>
                  <Box flexGrow={1}>
                    <Skeleton w="320px" height="40px" />
                  </Box>
                  {!editMode && <Skeleton w="108px" height="40px" />}
                  <Skeleton w="92px" height="40px" />
                  <Skeleton w="80px" height="40px" />
                </HStack>
                <VStack w="100%" spacing={6} alignItems="flex-start">
                  <SkeletonText w="50%" mt="4" noOfLines={7} spacing="4" />
                  <Skeleton w="100%" height="40px" />
                  <SkeletonText w="100%" mt="4" noOfLines={5} spacing="4" />
                  <Skeleton w="100%" height="40px" />
                </VStack>
              </VStack>
            )}

          {!isLoading && !isOdmaUrlLoading && editMode && (
            <MembershipRenewalForm
              businessId={businessId!}
              membershipRenewal={membershipRenewal}
              onSuccess={onSuccess}
              onFailure={onFailure}
              onCancel={onCancel}
            />
          )}

          {!isLoading && !isOdmaUrlLoading && !editMode && membershipRenewal && (
            <>
              <HStack w="100%">
                <Box flexGrow={2} pb={4}>
                  <CopyText
                    text={membershipRenewal?.Meta?.Id!}
                    hover
                    adjustMargin
                  >
                    <Text fontSize="md" color="cherryUi.600">
                      {membershipRenewal?.id} /{" "}
                      {membershipRenewal?.MembershipRenewalFormConfigurationNo}
                    </Text>
                  </CopyText>
                  <Text fontSize="xl" color="cherryUi.600">
                    <b>{membershipRenewal?.FriendlyName}</b>
                  </Text>
                </Box>

                <Box>
                  {odmaUrl?.Value != null && (
                    <PreviewButton
                      url={odmaUrl?.Value as string}
                      variant="cherryButton"
                    >
                      Open
                    </PreviewButton>
                  )}
                </Box>

                <Box>
                  {odmaUrl?.Value != null && (
                    <CopyText
                      text={odmaUrl?.Value as string}
                      variant="cherryButton"
                    >
                      Copy Url
                    </CopyText>
                  )}
                </Box>

                <Box>
                  <BusinessMenu
                    businessId={businessId!}
                    onClick={() => setEditMode(true)}
                  />
                </Box>
              </HStack>

              <HStack w="100%" pb={6}>
                <VStack alignItems="flex-start" w="100%">
                  <ViewControl
                    label="Enabled"
                    value={membershipRenewal?.IsEnabled ? "Yes" : "No"}
                  />

                  <ViewControl
                    label="Redesign Enabled"
                    value={
                      membershipRenewal?.ODMARenewalsRedesignEnabled
                        ? "Yes"
                        : "No"
                    }
                  />

                  <ViewControl
                    label="Redirect URL"
                    value={membershipRenewal.RedirectUrl ?? "-"}
                  />

                  <ViewControl
                    label="Check Code"
                    value={membershipRenewal?.CheckCode ?? "-"}
                  />

                  <ViewControl
                    label="Venue ID"
                    value={membershipRenewal?.VenueId ?? "-"}
                  />

                  <ViewControl
                    label="Page Title"
                    value={membershipRenewal?.PageTitle ?? "-"}
                  />

                  <ViewControl
                    label="Patron Instructions"
                    value={membershipRenewal?.PatronInstructions ?? "-"}
                  />

                  <ViewControl label="Privacy Policy URL">
                    <Box flex={3}>
                      {(membershipRenewal?.PrivacyPolicyUrl && (
                        <PreviewButton
                          url={`${membershipRenewal?.PrivacyPolicyUrl}`}
                          adjustMargin
                        >
                          {membershipRenewal?.PrivacyPolicyUrl}
                        </PreviewButton>
                      )) ??
                        "-"}
                    </Box>
                  </ViewControl>

                  <ViewControl label="Terms and Conditions URL">
                    <Box flex={3}>
                      {(membershipRenewal?.TermsConditionsUrl && (
                        <PreviewButton
                          url={`${membershipRenewal?.TermsConditionsUrl}`}
                          adjustMargin
                        >
                          {membershipRenewal?.TermsConditionsUrl}
                        </PreviewButton>
                      )) ??
                        "-"}
                    </Box>
                  </ViewControl>

                  <ViewControl
                    label="Member Filter Query String"
                    value={membershipRenewal?.MemberFilterQuerystring ?? "-"}
                  />

                  <ViewControl
                    label="Invoice Filtering Method"
                    value={membershipRenewal?.InvoiceFilteringMethod ?? "-"}
                  />
                </VStack>

                {membershipRenewal?.LogoFileUrl && (
                  <Box alignSelf="flex-start">
                    <Image
                      maxHeight="200px"
                      objectFit="contain"
                      src={membershipRenewal?.LogoFileUrl}
                    />
                  </Box>
                )}
              </HStack>

              <Accordion
                w="100%"
                defaultIndex={[0, 1, 2, 3, 4, 5, 6]}
                allowToggle
                allowMultiple
                variant="section"
              >
                <AccordionItem>
                  {({ isExpanded }) => (
                    <>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <b>Form Header and Footer Content</b>
                          </Box>
                          {isExpanded ? (
                            <ChevronUpIcon color="white" fontSize="xl" />
                          ) : (
                            <AccordionIcon />
                          )}
                        </AccordionButton>
                      </h2>
                      <AccordionPanel width="100%">
                        <VStack w="100%" alignItems="flex-start">
                          <ViewControl label="Header Text">
                            <Box
                              flex={3}
                              fontSize="xs"
                              dangerouslySetInnerHTML={{
                                __html: membershipRenewal?.HeaderText ?? "",
                              }}
                            />
                          </ViewControl>
                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />

                          <ViewControl label="Footer Text">
                            <Box
                              flex={3}
                              fontSize="xs"
                              dangerouslySetInnerHTML={{
                                __html: membershipRenewal?.FooterText ?? "",
                              }}
                            />
                          </ViewControl>
                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />

                          <ViewControl label="Successful Renewal Text">
                            <Box
                              flex={3}
                              fontSize="xs"
                              dangerouslySetInnerHTML={{
                                __html:
                                  membershipRenewal?.SuccessfulRenewalText ??
                                  "",
                              }}
                            />
                          </ViewControl>
                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />

                          <ViewControl label="Member Not Found Text">
                            <Box
                              flex={3}
                              fontSize="xs"
                              dangerouslySetInnerHTML={{
                                __html:
                                  membershipRenewal?.MemberNotFoundText ?? "-",
                              }}
                            />
                          </ViewControl>
                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />

                          <ViewControl label="Multiple Members Found Text">
                            <Box
                              flex={3}
                              fontSize="xs"
                              dangerouslySetInnerHTML={{
                                __html:
                                  membershipRenewal?.MultipleMembersFoundText ??
                                  "-",
                              }}
                            />
                          </ViewControl>
                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />

                          <ViewControl label="Renewal Period Not Started Text">
                            <Box
                              flex={3}
                              fontSize="xs"
                              dangerouslySetInnerHTML={{
                                __html:
                                  membershipRenewal?.RenewalPeriodNotStartedText ??
                                  "-",
                              }}
                            />
                          </ViewControl>
                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />

                          <ViewControl label="Renewal Period Ended Text">
                            <Box
                              flex={3}
                              fontSize="xs"
                              dangerouslySetInnerHTML={{
                                __html:
                                  membershipRenewal?.RenewalPeriodEndedText ??
                                  "-",
                              }}
                            />
                          </ViewControl>
                        </VStack>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>

                {membershipRenewal?.PaymentConfiguration && (
                  <AccordionItem>
                    {({ isExpanded }) => (
                      <>
                        <h2>
                          <AccordionButton>
                            <Box flex="1" textAlign="left">
                              <b>Payment Configuration</b>
                            </Box>
                            {isExpanded ? (
                              <ChevronUpIcon color="white" fontSize="xl" />
                            ) : (
                              <AccordionIcon />
                            )}
                          </AccordionButton>
                        </h2>
                        <AccordionPanel pb={4}>
                          <VStack alignItems="flex-start" w="100%">
                            <ViewControl
                              label="Payment Provider"
                              value={getPaymentProviderLabel(
                                membershipRenewal?.PaymentConfiguration
                                  ?.PaymentProvider
                              )}
                            />

                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />

                            <ViewControl
                              label="Allow Payment by Points Redemption"
                              value={
                                membershipRenewal?.PaymentConfiguration
                                  ?.AllowPaymentByPointsRedemption
                                  ? "Yes"
                                  : "No"
                              }
                            />

                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />

                            <ViewControl
                              label="Allow split payments"
                              value={
                                membershipRenewal?.PaymentConfiguration
                                  ?.AllowSplitPayments
                                  ? "Yes"
                                  : "No"
                              }
                            />

                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />

                            <ViewControl
                              label="Enforce maximum point redemption value"
                              value={
                                membershipRenewal?.PaymentConfiguration
                                  ?.EnforceMaximumPointRedemptionValue
                                  ? "Yes"
                                  : "No"
                              }
                            />

                            {membershipRenewal?.PaymentConfiguration
                              ?.PaymentProvider === "Stripe" && (
                              <>
                                <Divider
                                  variant="section"
                                  marginInlineStart="-1rem !important"
                                  w="calc(100% + 2rem)"
                                />

                                <ViewControl
                                  label="Account ID"
                                  value={
                                    membershipRenewal.PaymentConfiguration
                                      .StripeConfiguration?.AccountId ?? "-"
                                  }
                                />
                                <Divider
                                  variant="section"
                                  marginInlineStart="-1rem !important"
                                  w="calc(100% + 2rem)"
                                />

                                <ViewControl
                                  label="Currency"
                                  value={
                                    membershipRenewal.PaymentConfiguration
                                      .StripeConfiguration?.ISOCurrencyCode ??
                                    "-"
                                  }
                                />
                                <Divider
                                  variant="section"
                                  marginInlineStart="-1rem !important"
                                  w="calc(100% + 2rem)"
                                />

                                <ViewControl
                                  label="Order Description"
                                  value={
                                    membershipRenewal.PaymentConfiguration
                                      .StripeConfiguration?.OrderDescription ??
                                    "-"
                                  }
                                />
                                <Divider
                                  variant="section"
                                  marginInlineStart="-1rem !important"
                                  w="calc(100% + 2rem)"
                                />

                                <ViewControl
                                  label="Use on behalf of account ID?"
                                  value={
                                    membershipRenewal.PaymentConfiguration
                                      .StripeConfiguration
                                      ?.UseOnBehalfOfAccountId
                                      ? "Yes"
                                      : "No"
                                  }
                                />
                                <Divider
                                  variant="section"
                                  marginInlineStart="-1rem !important"
                                  w="calc(100% + 2rem)"
                                />

                                <ViewControl
                                  label="Use automatic payment method?"
                                  value={
                                    membershipRenewal.PaymentConfiguration
                                      .StripeConfiguration
                                      ?.UseAutomaticPaymentMethod
                                      ? "Yes"
                                      : "No"
                                  }
                                />

                                <Divider
                                  variant="section"
                                  marginInlineStart="-1rem !important"
                                  w="calc(100% + 2rem)"
                                />

                                <ViewControl
                                  label="Minimum Transaction Amount"
                                  value={
                                    membershipRenewal.PaymentConfiguration.StripeConfiguration?.MinimumTransactionAmount?.toFixed(
                                      1
                                    ) ?? "-"
                                  }
                                />
                              </>
                            )}
                            {membershipRenewal?.PaymentConfiguration
                              ?.PaymentProvider == "Worldpay" && (
                              <>
                                <Divider
                                  variant="section"
                                  marginInlineStart="-1rem !important"
                                  w="calc(100% + 2rem)"
                                />

                                <ViewControl
                                  label="Account ID"
                                  value={
                                    membershipRenewal.PaymentConfiguration
                                      .WorldpayConfiguration?.AccountId ?? "-"
                                  }
                                />
                                <Divider
                                  variant="section"
                                  marginInlineStart="-1rem !important"
                                  w="calc(100% + 2rem)"
                                />

                                <ViewControl
                                  label="Order Description"
                                  value={
                                    membershipRenewal.PaymentConfiguration
                                      .WorldpayConfiguration
                                      ?.OrderDescription ?? "-"
                                  }
                                />
                                <Divider
                                  variant="section"
                                  marginInlineStart="-1rem !important"
                                  w="calc(100% + 2rem)"
                                />

                                <ViewControl
                                  label="Template Id"
                                  value={
                                    membershipRenewal.PaymentConfiguration
                                      .WorldpayConfiguration?.TemplateId ?? "-"
                                  }
                                />
                                <Divider
                                  variant="section"
                                  marginInlineStart="-1rem !important"
                                  w="calc(100% + 2rem)"
                                />

                                <ViewControl
                                  label="Minimum Transaction Amount"
                                  value={
                                    membershipRenewal.PaymentConfiguration.WorldpayConfiguration?.MinimumTransactionAmount?.toFixed(
                                      1
                                    ) ?? "-"
                                  }
                                />
                              </>
                            )}

                            {membershipRenewal?.PaymentConfiguration
                              ?.PaymentProvider === "NonIntegrated" && (
                              <>
                                <Divider
                                  variant="section"
                                  marginInlineStart="-1rem !important"
                                  w="calc(100% + 2rem)"
                                />

                                <ViewControl
                                  label="Order Description"
                                  value={
                                    membershipRenewal.PaymentConfiguration
                                      .NonIntegratedConfiguration
                                      ?.OrderDescription ?? "-"
                                  }
                                />
                              </>
                            )}
                          </VStack>
                        </AccordionPanel>
                      </>
                    )}
                  </AccordionItem>
                )}
                {membershipRenewal?.VenueNotificationEmailConfiguration && (
                  <AccordionItem>
                    {({ isExpanded }) => (
                      <>
                        <h2>
                          <AccordionButton>
                            <Box flex="1" textAlign="left">
                              <b>Venue Notification Email Configuration</b>
                            </Box>
                            {isExpanded ? (
                              <ChevronUpIcon color="white" fontSize="xl" />
                            ) : (
                              <AccordionIcon />
                            )}
                          </AccordionButton>
                        </h2>
                        <AccordionPanel pb={4}>
                          <VStack w="100%" alignItems="flex-start">
                            <ViewControl
                              label="Sender Name"
                              value={
                                membershipRenewal
                                  ?.VenueNotificationEmailConfiguration
                                  ?.SenderName ?? "-"
                              }
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />

                            <ViewControl
                              label="Sender Email"
                              value={
                                membershipRenewal
                                  ?.VenueNotificationEmailConfiguration
                                  ?.SenderEmailAddress ?? "-"
                              }
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />

                            <ViewControl
                              label="Template ID"
                              value={
                                membershipRenewal
                                  ?.VenueNotificationEmailConfiguration
                                  ?.TemplateId ?? "-"
                              }
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />

                            <ViewControl label="Recipient Email Addresses">
                              {(membershipRenewal
                                .VenueNotificationEmailConfiguration
                                ?.RecipientEmailAddresses &&
                                membershipRenewal
                                  .VenueNotificationEmailConfiguration
                                  ?.RecipientEmailAddresses?.length > 0 && (
                                  <UnorderedList flex={3}>
                                    {membershipRenewal.VenueNotificationEmailConfiguration.RecipientEmailAddresses.map(
                                      (item) => (
                                        <ListItem ml={4} key={item}>
                                          <Text>{item}</Text>
                                        </ListItem>
                                      )
                                    )}
                                  </UnorderedList>
                                )) || (
                                <Text flex={3} fontStyle="italic">
                                  Recipient email addresses has not been set
                                </Text>
                              )}
                            </ViewControl>
                          </VStack>
                        </AccordionPanel>
                      </>
                    )}
                  </AccordionItem>
                )}

                {membershipRenewal?.MemberReceiptEmailConfiguration && (
                  <AccordionItem>
                    {({ isExpanded }) => (
                      <>
                        <h2>
                          <AccordionButton>
                            <Box flex="1" textAlign="left">
                              <b>Member Receipt Email Configuration</b>
                            </Box>
                            {isExpanded ? (
                              <ChevronUpIcon color="white" fontSize="xl" />
                            ) : (
                              <AccordionIcon />
                            )}
                          </AccordionButton>
                        </h2>
                        <AccordionPanel pb={4}>
                          <VStack w="100%" alignItems="flex-start">
                            <ViewControl
                              label="Enabled"
                              value={
                                membershipRenewal?.MemberReceiptEmailEnabled
                                  ? "Yes"
                                  : "No"
                              }
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />
                            <ViewControl
                              label="Sender Name"
                              value={
                                membershipRenewal
                                  ?.MemberReceiptEmailConfiguration
                                  ?.SenderName ?? "-"
                              }
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />

                            <ViewControl
                              label="Sender Email"
                              value={
                                membershipRenewal
                                  ?.MemberReceiptEmailConfiguration
                                  ?.SenderEmailAddress ?? "-"
                              }
                            />
                            <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />

                            <ViewControl
                              label="Template ID"
                              value={
                                membershipRenewal
                                  ?.MemberReceiptEmailConfiguration
                                  ?.TemplateId ?? "-"
                              }
                            />
                            {/* <Divider
                              variant="section"
                              marginInlineStart="-1rem !important"
                              w="calc(100% + 2rem)"
                            />

                            <ViewControl label="Recipient Email Addresses">
                              {(membershipRenewal
                                .MemberReceiptEmailConfiguration
                                ?.RecipientEmailAddresses &&
                                membershipRenewal
                                  .MemberReceiptEmailConfiguration
                                  ?.RecipientEmailAddresses?.length > 0 && (
                                  <UnorderedList flex={3}>
                                    {membershipRenewal.MemberReceiptEmailConfiguration.RecipientEmailAddresses.map(
                                      (item) => (
                                        <ListItem ml={4} key={item}>
                                          <Text>{item}</Text>
                                        </ListItem>
                                      )
                                    )}
                                  </UnorderedList>
                                )) || (
                                <Text flex={3} fontStyle="italic">
                                  Recipient email addresses has not been set
                                </Text>
                              )}
                            </ViewControl> */}
                          </VStack>
                        </AccordionPanel>
                      </>
                    )}
                  </AccordionItem>
                )}

                <AccordionItem>
                  {({ isExpanded }) => (
                    <>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <b>Member Self-Service Configuration</b>
                          </Box>
                          {isExpanded ? (
                            <ChevronUpIcon color="white" fontSize="xl" />
                          ) : (
                            <AccordionIcon />
                          )}
                        </AccordionButton>
                      </h2>
                      <AccordionPanel pb={4}>
                        <VStack w="100%" alignItems="flex-start">
                          <ViewControl
                            label="Configuration Id"
                            value={
                              membershipRenewal?.MemberSelfServiceConfiguration
                                ?.MemberSelfServiceConfigurationId ?? "-"
                            }
                          />
                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />
                          <ViewControl
                            label="Mandatory"
                            value={
                              membershipRenewal?.MemberSelfServiceConfiguration
                                ?.IsMandatory
                                ? "Yes"
                                : "No"
                            }
                          />
                          <Divider
                            variant="section"
                            marginInlineStart="-1rem !important"
                            w="calc(100% + 2rem)"
                          />
                          <ViewControl
                            label="Post-Renewal Self-Service Link Text"
                            value={
                              membershipRenewal?.MemberSelfServiceConfiguration
                                ?.PostRenewalSelfServiceLinkText ?? "-"
                            }
                          />
                        </VStack>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>

                <AccordionItem>
                  {({ isExpanded }) => (
                    <>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <b>Security</b>
                          </Box>
                          {isExpanded ? (
                            <ChevronUpIcon color="white" fontSize="xl" />
                          ) : (
                            <AccordionIcon />
                          )}
                        </AccordionButton>
                      </h2>
                      <AccordionPanel pb={4}>
                        <VStack w="100%" alignItems="flex-start">
                          <ViewControl
                            label="OTP Verification Enabled"
                            value={
                              membershipRenewal?.OtpVerificationEnabled
                                ? "Yes"
                                : "No"
                            }
                          />
                          {membershipRenewal?.OtpVerificationEnabled && (
                            <>
                              <Divider
                                variant="section"
                                marginInlineStart="-1rem !important"
                                w="calc(100% + 2rem)"
                              />
                              <ViewControl
                                label="Preferred OTP Method"
                                value={
                                  membershipRenewal?.OtpPreferredMethod ?? "-"
                                }
                              />
                              <Divider
                                variant="section"
                                marginInlineStart="-1rem !important"
                                w="calc(100% + 2rem)"
                              />
                              <ViewControl
                                label="Return Preferred OTP Method Only"
                                value={
                                  membershipRenewal?.ReturnPreferredOtpMethodOnly
                                    ? "Yes"
                                    : "No"
                                }
                              />
                              <Divider
                                variant="section"
                                marginInlineStart="-1rem !important"
                                w="calc(100% + 2rem)"
                              />
                              <ViewControl
                                label="Allow Renewal when no OTP methods are available"
                                value={
                                  membershipRenewal?.AllowRenewalWhenMemberLacksOTPMethods
                                    ? "Yes"
                                    : "No"
                                }
                              />
                              <Divider
                                variant="section"
                                marginInlineStart="-1rem !important"
                                w="calc(100% + 2rem)"
                              />
                              <ViewControl
                                label="No OTP Methods Available Message"
                                value={
                                  membershipRenewal?.NoOTPMethodsAvailableText ??
                                  "-"
                                }
                              />
                              <Divider
                                variant="section"
                                marginInlineStart="-1rem !important"
                                w="calc(100% + 2rem)"
                              />
                              <ViewControl
                                label="OTP Valid Duration"
                                value={
                                  membershipRenewal?.OtpValidTimePeriod
                                    ? `${membershipRenewal.OtpValidTimePeriod.Quantity} ${membershipRenewal.OtpValidTimePeriod.Period}`
                                    : "-"
                                }
                              />
                            </>
                          )}
                        </VStack>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>

                <AccordionItem>
                  {({ isExpanded }) => (
                    <>
                      <h2>
                        <AccordionButton>
                          <Box flex="1" textAlign="left">
                            <b>Advanced Configuration</b>
                          </Box>
                          {isExpanded ? (
                            <ChevronUpIcon color="white" fontSize="xl" />
                          ) : (
                            <AccordionIcon />
                          )}
                        </AccordionButton>
                      </h2>
                      <AccordionPanel pb={4}>
                        <VStack w="100%" alignItems="flex-start">
                          <Box
                            width="100%"
                            borderWidth="1px"
                            borderRadius="lg"
                            boxShadow="lg"
                            overflowX="auto"
                            padding={6}
                            flex={3}
                          >
                            {membershipRenewal?.ThemeData ? (
                              <pre>
                                {JSON.stringify(
                                  membershipRenewal?.ThemeData,
                                  null,
                                  2
                                )}
                              </pre>
                            ) : (
                              ""
                            )}
                          </Box>
                        </VStack>
                      </AccordionPanel>
                    </>
                  )}
                </AccordionItem>
              </Accordion>
            </>
          )}
        </VStack>
      </PageContent>
    </>
  );
};
